import React from 'react';
import { Redirect } from 'react-router';

import { PageSize } from 'types/state';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServiceState } from 'hooks/useServiceState';
import { routes } from 'utils/routes';
import { Pagination } from 'design/pagination';
import { AdminContentLayout } from 'design/adminContentLayout';
import { AdminPageTitleLayout } from 'design/adminPageTitleLayout';
import { AdminPageHeadLayout } from 'design/adminPageHeadLayout';
import { PageSizeControl } from 'containers/PageSizeControl';
import { useTableSortingController } from 'hooks/useTableSortingController';
import { TableControlsLayout } from 'design/tableControlsLayout';
import { BillsTable } from 'containers/BillsTable/BillsTable';
import { Bill, BillsTableSortableField } from 'types/state/bill';
import { TextComponent } from 'design/text/textComponents';
import { SearchInputLayoutComponent } from 'design/searchInputLayout';
import { TextInputControlComponent } from 'design/textInputControl';
import { AdminNotificationPanel } from 'containers/AdminNotificationPanel';

type Props = {
  currentPage: number;
};

const MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY = 2;
const DEFAULT_SORT_FIELD: BillsTableSortableField = 'id';
const DEFAULT_SORT_ASCENDING_FLAG = false;

export const BillsPageContent = (props: Props) => {
  const { currentPage } = props;

  const { sortBy, tableSorting: sorting } = useTableSortingController({
    initialField: DEFAULT_SORT_FIELD,
    initialAscending: DEFAULT_SORT_ASCENDING_FLAG,
  });

  // Подключение интернационализации.
  const intl = useIntlDictionary();

  // Состояние хранит количество страниц, доступных для
  // загрузки. Состояние хранится отдельно, чтобы пагинация
  // не пропадала при каждом новом вызове сервиса.
  const [totalPagesCount, setTotalPagesCount] = React.useState<null | number>(
    null
  );

  // Состояние хранит текстовое значение по которому будет
  // производиться поиск платежей.
  const [searchValue, setSearchValue] = React.useState('');

  // Для осуществления поиска по текстовому значению подходит
  // только строка определенной длины. Чтобы не провоцоировать
  // лишний вызов сервиса на получение данных страницы, значения
  // поисковой строки меньше определенной длины приводятся к
  // одинаковому значению.
  const searchBy =
    searchValue.length < MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY
      ? null
      : searchValue;

  // Состояние хранит значение размера страницы.
  const [sizeValue, setSizeValue] = React.useState<PageSize>(20);

  // Обработчик изменившегося значения размера страницы.
  const onSizeValueChange = setSizeValue;

  // Подключение сервисов.
  const { getBillsPageData, downloadBillReport } = useServices();

  // Состояние и метод вызова сервиса на получение данных страницы.
  const [pageDataState, getPageDataByParams] = useServiceState(
    getBillsPageData
  );

  const getPageData = React.useCallback(() => {
    getPageDataByParams({
      page: currentPage,
      pageSize: sizeValue,
      sorting,
      searchBy: searchBy || undefined,
    });
  }, [currentPage, getPageDataByParams, searchBy, sizeValue, sorting]);

  // Эффект вызывает сервис на получение данных страницы после
  // монтирования и каждый раз, когда изменится один из параметров.
  // Используется useLayoutEfeect чтобы избежать мерцания индикатора
  // сортировки.
  React.useLayoutEffect(() => {
    getPageData();
  }, [getPageData]);

  // Эффект следит за состоянием сервиса на получение данных страницы
  // и обновляет значение доступного количества страниц.
  // Используется useLayoutEffect чтобы в случае несуществующей
  // страницы осуществить редирект без отрисовки пустых данных.
  React.useLayoutEffect(() => {
    if (pageDataState && pageDataState.resolvedWith) {
      setTotalPagesCount(pageDataState.resolvedWith.totalPagesCount);
    }
  }, [pageDataState]);

  // Список клиентов кэшируется в локальном состоянии.
  const [tableItems, setTableItems] = React.useState<null | Bill[]>(null);

  // Кэшированный список клиентов инвалидируется при смене страницы.
  React.useEffect(() => {
    setTableItems(null);
  }, []);

  // При получении нового списка клиентов он заменяет собой кэшированный список.
  React.useEffect(() => {
    const tableItems =
      pageDataState &&
      pageDataState.resolvedWith &&
      pageDataState.resolvedWith.bills;

    if (tableItems) {
      setTableItems(tableItems);
    }
  }, [pageDataState]);

  const handleTableItemClick = React.useCallback(
    (billId: number) => {
      const bill = tableItems?.find((item) => item.id === billId);

      if (bill) {
        downloadBillReport({
          billId,
          clientId: bill.clientId,
        });
      }
    },
    [downloadBillReport, tableItems]
  );

  // В случае, когда выбранная страница не существует,
  // осуществляется редирект на последнюю доступную страницу.
  // Ситуация возникает, например, когда переключается размер страницы.
  if (totalPagesCount !== null && currentPage > totalPagesCount) {
    if (totalPagesCount > 0) {
      return <Redirect to={routes.cardPayments(totalPagesCount)} />;
    } else if (currentPage > 1) {
      return <Redirect to={routes.cardPayments(1)} />;
    }
  }

  const paginationBasePath = routes.billsRoot();

  return (
    <AdminContentLayout
      headNode={
        <AdminPageHeadLayout>
          <AdminNotificationPanel />
          <AdminPageTitleLayout
            textNode={
              <TextComponent size="mp">
                {intl.adminNavigation.billsLinkText}
              </TextComponent>
            }
            actionNode={null}
          />
          <TableControlsLayout
            primaryControlsNode={
              <TextComponent size="s">
                <SearchInputLayoutComponent
                  control={
                    <TextInputControlComponent
                      placeholder={intl.billsPageSearchPlaceholder}
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                  }
                />
              </TextComponent>
            }
            secondaryControlsNode={
              <>
                {totalPagesCount !== null && (
                  <TextComponent size="s">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPagesCount}
                      basePath={paginationBasePath}
                    />
                  </TextComponent>
                )}
                <TextComponent size="s">
                  <PageSizeControl
                    value={sizeValue}
                    onValueChange={onSizeValueChange}
                  />
                </TextComponent>
              </>
            }
          ></TableControlsLayout>
        </AdminPageHeadLayout>
      }
      bodyNode={
        tableItems !== null && (
          <BillsTable
            bills={tableItems}
            sortedBy={sorting.field}
            ascendingSort={sorting.ascending}
            onSortChange={sortBy}
            onItemClick={handleTableItemClick}
          />
        )
      }
    />
  );
};
